import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { useState, use, useEffect, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { AppDataContext } from '@/lib/app-data-hydration';
import { AdminReferencePlaceTable } from './reference/AdminReferencePlaceTable';
import { AdminReferenceEventTable } from './reference/AdminReferenceEventTable';
import { AdminReferenceStats } from './reference/AdminReferenceStats';
import type { HistoricalEventType } from './reference/types';
import {
  HISTORICAL_EVENT_TYPES, COST_RATES,
  getPromptAndImageStats, getMissingPeriods, getMissingImages
} from './reference/utils';
import { DBCollection } from '@/lib/utils/constants-shared';

// Delay utility to respect API rate limits
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export default function AdminReferenceData() {
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');
  const [allPlacesLoaded, setAllPlacesLoaded] = useState(false);
  const [allEventsLoaded, setAllEventsLoaded] = useState(false);
  const [allPlacesExpanded, setAllPlacesExpanded] = useState(false);

  const [generatingPrompts, setGeneratingPrompts] = useState<Set<string>>(new Set<string>());
  const [generatingImages, setGeneratingImages] = useState<Set<string>>(new Set<string>());
  const [regeneratingPlaces, setRegeneratingPlaces] = useState<Set<string>>(new Set<string>());
  const [generatingEventTypes, setGeneratingEventTypes] = useState<Set<HistoricalEventType>>(new Set<HistoricalEventType>());
  const [regeneratingEventTypes, setRegeneratingEventTypes] = useState<Set<HistoricalEventType>>(new Set<HistoricalEventType>());
  const [batchRegeneratingAll, setBatchRegeneratingAll] = useState(false);
  const [viewingPromptsPlace, setViewingPromptsPlace] = useState<string | null>(null);

  const [placeImagePrompts, setPlaceImagePrompts] = useState<any[]>([]);
  const [eventImagePrompts, setEventImagePrompts] = useState<any[]>([]);

  const { refresh: globalRefresh } = use(AppDataContext);
  const [adminPlaces, setAdminPlaces] = useState<any[]>([]);
  const [adminHistoricalEvents, setAdminHistoricalEvents] = useState<any[]>([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Fetch all reference data directly from Firestore for management
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingCounts(true);
      const db = getFirestore();
      try {
        const [placePrompts, eventPrompts, placesSnap, eventsSnap] = await Promise.all([
          getDocs(collection(db, DBCollection.placeImagePrompts)),
          getDocs(collection(db, DBCollection.historicalEventImagePrompts)),
          getDocs(collection(db, DBCollection.places)),
          getDocs(collection(db, DBCollection.historicalEvents))
        ]);

        const pPrompts = placePrompts.docs.map(doc => ({
          placeName: doc.id,
          ...doc.data()
        }));
        const ePrompts = eventPrompts.docs.map(doc => ({
          eventId: doc.id,
          ...doc.data()
        }));

        const pList = placesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        const eList = eventsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log(`[Admin] Fetched ${pPrompts.length} place prompts, ${ePrompts.length} event prompts`);
        console.log(`[Admin] Fetched ${pList.length} places, ${eList.length} global events`);

        setPlaceImagePrompts(pPrompts);
        setEventImagePrompts(ePrompts);
        setAdminPlaces(pList);
        setAdminHistoricalEvents(eList);
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
      } finally {
        setIsLoadingCounts(false);
      }
    };
    fetchData();
  }, [refreshCounter]);

  const refreshAction = useCallback(async () => {
    setRefreshCounter(prev => prev + 1);
    await globalRefresh();
  }, [globalRefresh]);

  const getAdminPlaceByName = useCallback((name: string) => {
    return adminPlaces.find(p => p.placeName === name);
  }, [adminPlaces]);

  // Derived data
  const existingGeoRefNames = useMemo<Set<string>>(() => new Set((adminPlaces || []).map((p: any) => p.placeName)), [adminPlaces]);

  const imagePromptDetailsMap = useMemo(() => {
    const map = new Map<string, any>();
    placeImagePrompts.forEach((p: any) => {
      if (p.placeName) map.set(p.placeName, p);
    });
    return map;
  }, [placeImagePrompts]);

  const allPlaces = useMemo(() => {
    if (!adminPlaces) return [];
    const names = Array.from(new Set(adminPlaces.map((p: any) => p.placeName))) as string[];
    return names.sort();
  }, [adminPlaces]);

  const filteredPlaces = useMemo(() => {
    if (!placeSearchQuery) return allPlaces.map(name => ({ placeName: name }));
    return allPlaces
      .filter(name => name.toLowerCase().includes(placeSearchQuery.toLowerCase()))
      .map(name => ({ placeName: name }));
  }, [allPlaces, placeSearchQuery]);

  const stats = useMemo(() => {
    const result = getPromptAndImageStats(
      allPlaces, existingGeoRefNames, imagePromptDetailsMap, getAdminPlaceByName
    );
    console.log('[Admin] Stats calculated:', result);
    return result;
  }, [allPlaces, existingGeoRefNames, imagePromptDetailsMap, getAdminPlaceByName]);

  // Event Stats
  const totalHistoricalEvents = adminHistoricalEvents?.length || 0;

  const eventPromptsMap = useMemo(() => {
    const map = new Map<string, any>();
    eventImagePrompts.forEach(p => map.set(p.eventId || p.title, p));
    return map;
  }, [eventImagePrompts]);
  const eventsWithPromptsTotal = eventPromptsMap.size;
  const eventsWithImagesTotal = useMemo(() => {
    return adminHistoricalEvents?.filter((e: any) => e.image?.imageUrl).length || 0;
  }, [adminHistoricalEvents]);

  const eventTypeData = useMemo(() => {
    return HISTORICAL_EVENT_TYPES.map(eventType => {
      const items = adminHistoricalEvents?.filter((e: any) =>
        e.type === eventType.type ||
        e.eventType === eventType.type ||
        e.categories?.some((c: any) => c.category === eventType.type)
      ) || [];
      const existing = items.filter((e: any) => e.generationStatus === 'completed' || e.image?.generationStatus === 'completed').length;
      const missing = items.filter((e: any) => (e.generationStatus !== 'completed' && e.image?.generationStatus !== 'completed')).map((e: any) => e.id || e.title);
      return {
        ...eventType,
        total: items.length,
        existing,
        missing
      };
    });
  }, [adminHistoricalEvents]);

  // Handlers
  const handleRegenerateSinglePlace = async (placeName: string) => {
    setRegeneratingPlaces(prev => new Set(prev).add(placeName));
    try {
      const functions = getFunctions();
      const regenerate = httpsCallable(functions, 'generatePlaceData', { timeout: 600000 });
      await regenerate({ placeName });
      await refreshAction();
    } catch (error) {
      console.error('Failed to regenerate place:', error);
    } finally {
      setRegeneratingPlaces(prev => {
        const next = new Set(prev);
        next.delete(placeName);
        return next;
      });
    }
  };

  const handleGeneratePromptsForPlace = async (placeName: string) => {
    setGeneratingPrompts(prev => new Set(prev).add(placeName));
    try {
      const functions = getFunctions();
      const generate = httpsCallable(functions, 'generatePlaceImagePrompts', { timeout: 600000 });
      await generate({ placeName });
      await refreshAction();
    } catch (error) {
      console.error('Failed to generate prompts:', error);
    } finally {
      setGeneratingPrompts(prev => {
        const next = new Set(prev);
        next.delete(placeName);
        return next;
      });
    }
  };

  const handleGenerateImages = async (placeName: string) => {
    setGeneratingImages(prev => new Set(prev).add(placeName));
    try {
      const functions = getFunctions();
      const generate = httpsCallable(functions, 'generatePlaceEraImages', { timeout: 600000 });
      await generate({ placeName });
      await refreshAction();
    } catch (error) {
      console.error('Failed to generate images:', error);
    } finally {
      setGeneratingImages(prev => {
        const next = new Set(prev);
        next.delete(placeName);
        return next;
      });
    }
  };

  const handleGenerateMissingImages = async (placeName: string, missing: string[]) => {
    setGeneratingImages(prev => new Set(prev).add(placeName));
    try {
      const functions = getFunctions();
      const generate = httpsCallable(functions, 'generateMissingPlaceImages', { timeout: 600000 });
      await generate({ placeName });
      await refreshAction();
    } catch (error) {
      console.error('Failed to generate era images:', error);
    } finally {
      setGeneratingImages(prev => {
        const next = new Set(prev);
        next.delete(placeName);
        return next;
      });
    }
  };

  const handleBatchGenerateMissing = async () => {
    setBatchRegeneratingAll(true);
    try {
      const functions = getFunctions();
      const generateImages = httpsCallable(functions, 'generatePlaceEraImages', { timeout: 600000 });

      // 1. Find all places that have prompts but are missing images
      const incompletePlaces: { name: string, missing: string[] }[] = [];
      allPlaces.forEach(name => {
        const info = getMissingImages(name, imagePromptDetailsMap, getAdminPlaceByName);
        if (info.missing.length > 0) {
          incompletePlaces.push({ name, missing: info.missing });
        }
      });

      console.log(`[Admin] Starting batch generation for ${incompletePlaces.length} incomplete places...`);

      // 2. Process them serially with delay to respect rate limits (20 req/min)
      for (let i = 0; i < incompletePlaces.length; i++) {
        const item = incompletePlaces[i];
        setGeneratingImages(prev => new Set(prev).add(item.name));
        try {
          console.log(`[Admin] [${i + 1}/${incompletePlaces.length}] Generating missing images for ${item.name}: ${item.missing.join(', ')}`);
          await generateImages({ placeName: item.name, eras: item.missing });
          console.log(`[Admin] ✓ Successfully generated images for ${item.name}`);
        } catch (err) {
          console.error(`[Admin] ✗ Failed to generate images for ${item.name}:`, err);
        } finally {
          setGeneratingImages(prev => {
            const next = new Set(prev);
            next.delete(item.name);
            return next;
          });
        }

        // Wait 4 seconds between places to stay under 20 req/min limit
        if (i < incompletePlaces.length - 1) {
          console.log(`[Admin] Waiting 4 seconds before next place...`);
          await delay(4000);
        }
      }

      await refreshAction();
      console.log(`[Admin] Batch generation complete.`);
    } catch (error) {
      console.error('Batch generation failed:', error);
    } finally {
      setBatchRegeneratingAll(false);
    }
  };

  const handleBatchGenerateMissingType = async (type: 'ruins' | 'reconstructions') => {
    setBatchRegeneratingAll(true);
    try {
      const functions = getFunctions();
      const generateImages = httpsCallable(functions, 'generatePlaceEraImages', { timeout: 600000 });

      const incompletePlaces: { name: string, missing: string[] }[] = [];
      allPlaces.forEach(name => {
        const info = getMissingImages(name, imagePromptDetailsMap, getAdminPlaceByName);
        const filteredMissing = type === 'ruins'
          ? info.missing.filter(m => m === 'ruins')
          : info.missing.filter(m => m !== 'ruins');

        if (filteredMissing.length > 0) {
          incompletePlaces.push({ name, missing: filteredMissing });
        }
      });

      console.log(`[Admin] Starting batch generation for ${incompletePlaces.length} places (Type: ${type})...`);

      for (let i = 0; i < incompletePlaces.length; i++) {
        const item = incompletePlaces[i];
        setGeneratingImages(prev => new Set(prev).add(item.name));
        try {
          console.log(`[Admin] [${i + 1}/${incompletePlaces.length}] Generating ${type} for ${item.name}`);
          await generateImages({ placeName: item.name, eras: item.missing });
          console.log(`[Admin] ✓ Successfully generated ${type} for ${item.name}`);
        } catch (err) {
          console.error(`[Admin] ✗ Failed to generate ${type} for ${item.name}:`, err);
        } finally {
          setGeneratingImages(prev => {
            const next = new Set(prev);
            next.delete(item.name);
            return next;
          });
        }

        // Wait 4 seconds between places to stay under 20 req/min limit
        if (i < incompletePlaces.length - 1) {
          console.log(`[Admin] Waiting 4 seconds before next place...`);
          await delay(4000);
        }
      }

      await refreshAction();
    } catch (error) {
      console.error(`Batch ${type} generation failed:`, error);
    } finally {
      setBatchRegeneratingAll(false);
    }
  };

  const handleGenerateEventType = async (type: HistoricalEventType, missing: string[]) => {
    setGeneratingEventTypes(prev => new Set(prev).add(type));
    try {
      const functions = getFunctions();
      const generate = httpsCallable(functions, 'generateHistoricalEventsBatch', { timeout: 600000 });
      await generate({ type, ids: missing });
      await refreshAction();
    } catch (error) {
      console.error(`Failed to generate ${type}:`, error);
    } finally {
      setGeneratingEventTypes(prev => {
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
    }
  };

  const handleRegenerateEventType = async (type: HistoricalEventType) => {
    setRegeneratingEventTypes(prev => new Set(prev).add(type));
    try {
      const functions = getFunctions();
      const regenerate = httpsCallable(functions, 'regenerateHistoricalEventsByType', { timeout: 600000 });
      await regenerate({ type });
      await refreshAction();
    } catch (error) {
      console.error(`Failed to regenerate ${type}:`, error);
    } finally {
      setRegeneratingEventTypes(prev => {
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
    }
  };

  useEffect(() => {
    if (adminPlaces.length > 0) setAllPlacesLoaded(true);
    if (adminHistoricalEvents.length > 0) setAllEventsLoaded(true);
  }, [adminPlaces, adminHistoricalEvents]);

  return (
    <div className="space-y-8">
      {/* Places Section */}
      <Card className="border-l-4 border-l-blue-600">
        <CardContent className="pt-6 space-y-6">
          <AdminReferenceStats
            allPlacesLoaded={allPlacesLoaded}
            promptAndImageStats={stats}
            batchRegeneratingAll={batchRegeneratingAll}
            totalPlaces={allPlaces.length}
            totalExistingPlaces={existingGeoRefNames.size}
            costRates={COST_RATES}
            onBatchGeneratePrompts={handleBatchGenerateMissing}
            onBatchGenerateMissingType={handleBatchGenerateMissingType}
            onBatchRegenerateOld={() => { }}
          />

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search places..."
                value={placeSearchQuery}
                onChange={(e) => setPlaceSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {placeSearchQuery && (
              <Button size="sm" variant="ghost" onClick={() => setPlaceSearchQuery('')}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <AdminReferencePlaceTable
            filteredPlaces={filteredPlaces}
            allPlacesExpanded={allPlacesExpanded}
            onToggleExpand={() => setAllPlacesExpanded(!allPlacesExpanded)}
            existingGeoRefNames={existingGeoRefNames}
            imagePromptDetails={imagePromptDetailsMap}
            getPlaceByName={getAdminPlaceByName}
            generatingImages={generatingImages}
            generatingPrompts={generatingPrompts}
            regeneratingPlaces={regeneratingPlaces}
            getMissingPeriods={(name) => getMissingPeriods(name, imagePromptDetailsMap, getAdminPlaceByName)}
            getMissingImages={(name) => getMissingImages(name, imagePromptDetailsMap, getAdminPlaceByName)}
            onRegeneratePlace={handleRegenerateSinglePlace}
            onGeneratePrompts={handleGeneratePromptsForPlace}
            onGenerateImages={handleGenerateImages}
            onGenerateMissingImages={handleGenerateMissingImages}
            onViewPrompts={setViewingPromptsPlace}
          />
        </CardContent>
      </Card>

      {/* Events Section */}
      <AdminReferenceEventTable
        eventTypeData={eventTypeData}
        totalHistoricalEvents={totalHistoricalEvents}
        eventsWithPrompts={eventsWithPromptsTotal}
        eventsWithImages={eventsWithImagesTotal}
        eventPromptsProgress={totalHistoricalEvents > 0 ? (eventsWithPromptsTotal / totalHistoricalEvents) * 100 : 0}
        eventImagesProgress={totalHistoricalEvents > 0 ? (eventsWithImagesTotal / totalHistoricalEvents) * 100 : 0}
        generatingEventTypes={generatingEventTypes}
        regeneratingEventTypes={regeneratingEventTypes}
        onGenerateEventType={handleGenerateEventType}
        onRegenerateEventType={handleRegenerateEventType}
        onBatchGeneratePrompts={() => { }}
        onBatchGenerateImages={() => { }}
      />
    </div>
  );
}
